/**
 * Total-order comparator for sorting pipeline cards by a property value.
 *
 * The board fetches items already sorted by the backend, then re-sorts each
 * stage client-side so cards stay ordered after optimistic drag moves. This
 * comparator therefore has to reproduce a sensible, type-aware order — the
 * previous implementation compared everything as strings, which corrupted the
 * on-load order three ways:
 *   1. a missing value became `''`, and `Number('') === 0`, so blank cards were
 *      sorted as if their value were zero (mixed in among real zeros);
 *   2. when one side was a quantity object and the other missing, comparison
 *      fell through to `String({ amount }) === '[object Object]'` vs `''`,
 *      producing meaningless order;
 *   3. datetimes were compared lexically, so any inconsistency in the
 *      serialized form (offset vs `Z`, microsecond precision) misordered them.
 *
 * Rules:
 *   - Missing/empty values always sort LAST, in both directions.
 *   - Quantities (`{ amount }`), numbers, and numeric strings compare
 *     numerically.
 *   - Date-like strings compare by parsed timestamp (serialization-independent).
 *   - Everything else compares as a locale-aware, natural-numeric string.
 */

import { getPropertyValue } from '@/types/crm';

interface PropertyBag {
  properties?: Record<string, { value?: unknown } | string | null | undefined>;
}

/** Matches a leading ISO-8601 date (`YYYY-MM-DD…`) to gate date parsing. */
const DATE_LIKE = /^\d{4}-\d{2}-\d{2}/;

/** Read the raw value of `propertyName` from an item's property bag. */
function readValue(obj: unknown, propertyName: string): unknown {
  const bag = obj as PropertyBag;
  const propValue = bag.properties?.[propertyName];
  if (propValue == null) {
    return null;
  }
  // getPropertyValue maps arrays element-by-element, which would change sort
  // order for array-valued properties — keep those passed through unchanged.
  return Array.isArray(propValue)
    ? propValue
    : (getPropertyValue<unknown>(propValue) ?? null);
}

type Comparable =
  | { kind: 'missing' }
  | { kind: 'number'; value: number }
  | { kind: 'string'; value: string };

/** Classify a raw property value into a comparable, typed form. */
function toComparable(raw: unknown): Comparable {
  if (raw == null || raw === '') {
    return { kind: 'missing' };
  }
  // Quantity value objects carry an `amount` that is usually a number but can
  // arrive as a numeric string (e.g. a serialized Decimal like '42.50'). Coerce
  // both forms, consistent with how deal-card formatters read quantity amounts.
  if (typeof raw === 'object' && raw !== null && 'amount' in raw) {
    const { amount } = raw as { amount: unknown };
    if (typeof amount === 'number') {
      return Number.isFinite(amount)
        ? { kind: 'number', value: amount }
        : { kind: 'missing' };
    }
    if (typeof amount === 'string' && amount.trim() !== '') {
      const parsed = Number(amount.trim());
      if (Number.isFinite(parsed)) {
        return { kind: 'number', value: parsed };
      }
    }
    return { kind: 'missing' };
  }
  if (typeof raw === 'number') {
    return Number.isFinite(raw)
      ? { kind: 'number', value: raw }
      : { kind: 'missing' };
  }
  if (typeof raw === 'boolean') {
    return { kind: 'number', value: raw ? 1 : 0 };
  }
  const str = String(raw);
  const trimmed = str.trim();
  if (trimmed === '') {
    return { kind: 'missing' };
  }
  // Numeric strings (e.g. '42', '3.14') compare numerically.
  const asNumber = Number(trimmed);
  if (Number.isFinite(asNumber)) {
    return { kind: 'number', value: asNumber };
  }
  // Date-like strings compare by timestamp so ordering is independent of the
  // exact serialized form (offset vs 'Z', microsecond precision, etc.).
  if (DATE_LIKE.test(trimmed)) {
    const ts = Date.parse(trimmed);
    if (!Number.isNaN(ts)) {
      return { kind: 'number', value: ts };
    }
  }
  return { kind: 'string', value: str };
}

export function compareByProperty<T>(
  propertyName: string,
  order: 'asc' | 'desc'
): (a: T, b: T) => number {
  const direction = order === 'desc' ? -1 : 1;
  return (a: T, b: T): number => {
    const av = toComparable(readValue(a, propertyName));
    const bv = toComparable(readValue(b, propertyName));

    // Missing values sink to the bottom regardless of sort direction.
    if (av.kind === 'missing' || bv.kind === 'missing') {
      if (av.kind === 'missing' && bv.kind === 'missing') {
        return 0;
      }
      return av.kind === 'missing' ? 1 : -1;
    }

    if (av.kind === 'number' && bv.kind === 'number') {
      if (av.value === bv.value) {
        return 0;
      }
      return (av.value < bv.value ? -1 : 1) * direction;
    }

    // Mixed number/string or both strings: compare as strings. `numeric: true`
    // gives natural ordering (e.g. 'Item 2' before 'Item 10').
    const aStr = av.kind === 'number' ? String(av.value) : av.value;
    const bStr = bv.kind === 'number' ? String(bv.value) : bv.value;
    return (
      aStr.localeCompare(bStr, undefined, {
        numeric: true,
        sensitivity: 'base',
      }) * direction
    );
  };
}
